import { findStickerAtPoint, getStickerTargetUrl } from './stickerTargetUrl.js';

// Délai de survol avant affichage du tooltip (ms)
const TOOLTIP_DELAY = 600;
const MAX_CONTENT_LENGTH = 200;

const escapeHtml = (str) => String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const NBSP_REGEX = new RegExp(String.fromCharCode(160), 'g');

// DOMParser ne charge aucune ressource (contrairement à un innerHTML sur un div)
const htmlToText = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(String(html).replace(/<br\s*\/?>/gi, '\n'), 'text/html');
    return (doc.body.textContent || '').replace(NBSP_REGEX, ' ').trim();
};

const truncate = (str) => str.length > MAX_CONTENT_LENGTH ? `${str.slice(0, MAX_CONTENT_LENGTH).trim()}…` : str;

const pad = (n) => String(n).padStart(2, '0');

const countdownDate = (data) => {
    if (!data.date) return '';
    const [y, m, d] = String(data.date).split('-');
    const date = y && m && d ? `${d}/${m}/${y}` : String(data.date);
    const hasTime = data.hours !== false && data.hours != null && data.minutes !== false && data.minutes != null;
    return hasTime ? `${date} ${pad(data.hours)}:${pad(data.minutes)}` : date;
};

const musicLabel = (music) => {
    if (!music) return '';
    if (music.track) return [music.track.title, music.track.display_artist].filter(Boolean).join(' - ');
    const sound = music.original_sound;
    if (!sound) return '';
    return [sound.original_audio_title, sound.ig_artist && sound.ig_artist.full_name].filter(Boolean).join(' - ');
};

const mentionLabel = (mention) => {
    if (!mention) return '';
    const label = mention.pk ? mention.id : (mention.name || mention.id);
    return label ? `@${String(label).replace(/^@/, '')}` : '';
};

// Titre (type de widget) et lignes de contenu adaptées à chaque widget
export function getStickerTooltipParts(sticker, network, t) {
    const data = sticker.data || {};

    switch (sticker.type) {
        case 'link':
            return { title: t('dash.post-creator.story.sticker.link'), lines: [getStickerTargetUrl(sticker, network)] };
        case 'mention':
            return { title: t('dash.post-creator.story.sticker.mention'), lines: [mentionLabel(data.mention)] };
        case 'hashtag': {
            const tag = htmlToText(data.hashtag).replace(/^#/, '');
            return { title: t('dash.post-creator.media-editor.palette.widget-hashtag'), lines: [tag && `#${tag}`] };
        }
        case 'localisation':
            return { title: t('dash.post-creator.story.sticker.localisation'), lines: [data.place && data.place.name] };
        case 'question':
            return { title: t('dash.post-creator.story.sticker.question'), lines: [data.question] };
        case 'survey':
            return {
                title: t('dash.post-creator.story.sticker.survey'),
                lines: [data.question, ...(data.responses || []).filter(Boolean).map(r => `• ${r}`)],
            };
        case 'slider':
            return { title: t('dash.posts.slider'), lines: [[data.question, data.emoji].filter(Boolean).join(' ')] };
        case 'countdown':
            return { title: t('dash.post-creator.story.sticker.countdown'), lines: [data.name, countdownDate(data)] };
        case 'product':
            return {
                title: t('dash.post-creator.story.sticker.product'),
                lines: (data.products || []).map(p => p.text).filter(Boolean),
            };
        case 'music':
            return { title: t('dash.post-creator.story.sticker.music'), lines: [musicLabel(data.music)] };
        case 'text':
            return { title: t('dash.post-creator.story.sticker.text'), lines: [htmlToText(data.text)] };
        case 'gif':
            return { title: t('dash.post-creator.media-editor.palette.widget-gif'), lines: [data.gif && data.gif.title] };
        case 'image':
            return { title: t('dash.post-creator.story.sticker.photo'), lines: [] };
        default:
            return null;
    }
}

export function getStickerTooltipHtml(sticker, network, t) {
    const parts = getStickerTooltipParts(sticker, network, t);
    if (!parts) return null;
    const lines = parts.lines.filter(Boolean).map(l => escapeHtml(truncate(String(l).trim())).replace(/\n/g, '<br>'));
    return [`<b>${escapeHtml(parts.title)}</b>`, ...lines].join('<br>');
}

// Mixin : tooltip maison au survol prolongé d'un widget dans la preview (calque .lock)
// Le composant doit fournir les computed `previewStickers` et `previewNetwork`
export default {
    data() {
        return {
            hoveredStickerId: null,
            previewTooltipTimeout: null,
            previewTooltipShown: false,
            previewMouseFrame: null,
        }
    },

    methods: {
        handlePreviewMouseMove(e) {
            // throttle sur une frame : elementsFromPoint est coûteux
            if (this.previewMouseFrame) return;
            const { clientX, clientY } = e;
            this.previewMouseFrame = requestAnimationFrame(() => {
                this.previewMouseFrame = null;
                this.updateHoveredSticker(clientX, clientY);
            });
        },

        updateHoveredSticker(x, y) {
            const hit = this.focusedSticker ? null : findStickerAtPoint(this.$el, this.previewStickers, x, y);
            const id = hit ? hit.sticker.id : null;
            if (id === this.hoveredStickerId) return;

            this.hidePreviewTooltip();
            this.hoveredStickerId = id;
            if (!hit) return;

            const wrapper = hit.element.closest('.story-maker-draggable-sticker, .link-sticker-fixed');
            this.previewTooltipTimeout = setTimeout(() => this.showPreviewTooltip(hit.sticker, wrapper), TOOLTIP_DELAY);
        },

        showPreviewTooltip(sticker, wrapper) {
            const html = getStickerTooltipHtml(sticker, this.previewNetwork, this.$t.bind(this));
            if (!html || !wrapper || !document.body.contains(wrapper)) return;

            // Cible factice : le tooltip lit offsetWidth/Height, faux sur un sticker tourné/redimensionné
            const rect = wrapper.getBoundingClientRect();
            const target = {
                getBoundingClientRect: () => rect,
                offsetWidth: rect.width,
                offsetHeight: rect.height,
            };

            this.$store.commit('tooltip/updateTooltipContent', html);
            // 'wide' : classe CSS élargie, positionnée comme "top" (cas par défaut du tooltip)
            this.$store.commit('tooltip/updateTooltipPosition', 'wide');
            this.$store.commit('tooltip/updateTooltipElementTarget', target);
            this.$store.commit('tooltip/updateTooltipDisplay', true);
            this.previewTooltipShown = true;
        },

        hidePreviewTooltip() {
            if (this.previewTooltipTimeout) {
                clearTimeout(this.previewTooltipTimeout);
                this.previewTooltipTimeout = null;
            }
            if (this.previewTooltipShown) {
                this.$store.commit('tooltip/updateTooltipDisplay', false);
                this.$store.commit('tooltip/updateTooltipElementTarget', null);
                this.previewTooltipShown = false;
            }
        },

        handlePreviewMouseLeave() {
            this.hoveredStickerId = null;
            this.hidePreviewTooltip();
        },
    },

    watch: {
        focusedSticker() {
            this.handlePreviewMouseLeave();
        },
    },

    beforeDestroy() {
        if (this.previewMouseFrame) cancelAnimationFrame(this.previewMouseFrame);
        this.hidePreviewTooltip();
    },
}
